import {
  ArrowUpRightIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  InfoIcon,
  MapPinIcon,
  UsersIcon,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { InformationText } from "@/components/app/information-text";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useT } from "@/i18n";
import { eventWhen } from "@/i18n/format";
import { fieldNameOf } from "@/i18n/labels";
import { preloadable } from "@/lib/preload";
import { tournamentHref } from "@/lib/tournament";
import type { BoardBundle } from "../../../shared/board";
import { infoFields } from "../../../shared/board-info";
import { playMinutes } from "../../../shared/game-length";
import { type TournamentLocation, tournamentLocations } from "../../../shared/locations";
import { formatFor } from "../../../shared/settings";
import type { TournamentSummary } from "../../../shared/tournament";
import { TournamentAnnouncements } from "./TournamentAnnouncements";
import {
  TournamentContacts,
  TournamentDetailsInfo,
  TournamentKeyTimes,
} from "./TournamentDetailsInfo";
import { TournamentMedia } from "./TournamentMedia";

const mapChooser = preloadable(() => import("./MapChooser").then((m) => m.MapChooser));

/** Before play, the decision is when/where to attend and how to enter.
 * Public facts lead; empty operational setup stays out of this surface. */
export function TournamentInfo({
  meta,
  bundle,
  registrationOpen,
}: {
  meta: TournamentSummary;
  bundle: BoardBundle;
  registrationOpen: boolean;
}) {
  const t = useT();
  const { teams, divisions, pools, fields, schedule, settings, regulations } = bundle;
  const orderedDivisions = [...divisions].sort((a, b) => a.order - b.order);
  const locations = tournamentLocations(meta);
  const visibleFields = infoFields(fields, schedule);
  const details = meta.information?.details ?? {};
  const hasContacts = [
    details.lostFoundInstructions,
    details.lostFoundName,
    details.lostFoundPhone,
    details.lostFoundEmail,
    details.lostFoundLink,
    details.organiserName,
    details.organiserPhone,
    details.organiserEmail,
    details.organiserLink,
    details.firstAidName,
    details.firstAidPhone,
    details.firstAidEmail,
    details.firstAidLink,
    details.firstAidLocation,
  ].some(Boolean);
  const hasGuides = Boolean(
    registrationOpen ||
      meta.information?.text.trim() ||
      meta.information?.media.length ||
      [
        details.showers,
        details.changingRooms,
        details.food,
        details.accessibility,
        details.feeAmount,
        details.feeIncludes,
        details.paymentInstructions,
        details.paymentDeadline,
      ].some(Boolean),
  );
  const [selectedLocation, setSelectedLocation] = useState<TournamentLocation | null>(null);
  const MapChooser = mapChooser.useComponent();
  const formats =
    orderedDivisions.length > 0
      ? orderedDivisions.map((division) => ({ id: division.id, name: division.name }))
      : [{ id: undefined, name: t("board.info.format") }];

  return (
    <>
      <TournamentAnnouncements
        notices={meta.information?.announcements ?? []}
        timezone={meta.timezone}
      />
      <Card className="gap-0 p-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-5 py-4 text-sm adm:px-6">
          <CalendarDaysIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <time dateTime={meta.startDate} className="font-medium">
            {eventWhen(meta.startDate)}
          </time>
          {meta.endDate !== meta.startDate && (
            <>
              <span className="text-muted-foreground">–</span>
              <time dateTime={meta.endDate} className="font-medium">
                {eventWhen(meta.endDate)}
              </time>
            </>
          )}
          {meta.endTime && (
            <span className="text-muted-foreground">
              {t("board.info.ends")}: {meta.endTime}
            </span>
          )}
          {teams.length > 0 && (
            <a
              href={tournamentHref(meta.slug, "#teams")}
              className="ml-auto inline-flex items-center gap-2 text-brand-ink hover:underline underline-offset-4"
            >
              <UsersIcon className="size-4" aria-hidden="true" />
              {t("front.stat.teams", { count: teams.length })}
              <ChevronRightIcon className="size-4" aria-hidden="true" />
            </a>
          )}
        </div>
        <div className="divide-y">
          {locations.length ? (
            locations.map((location) => (
              <button
                key={location.id}
                type="button"
                className="flex w-full items-center gap-4 px-5 py-6 text-left transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring adm:px-6"
                aria-label={`${location.name}: ${t("board.info.map")}`}
                onPointerEnter={() => mapChooser.warm()}
                onFocus={() => mapChooser.warm()}
                onClick={() => {
                  void mapChooser.load().then((loaded) => {
                    if (loaded) setSelectedLocation(location);
                  });
                }}
              >
                <MapPinIcon className="size-6 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-xl font-semibold tracking-tight adm:text-2xl">
                    {location.name}
                  </span>
                  {(location.address || (meta.city && meta.city !== location.name)) && (
                    <span className="mt-1 block break-words text-sm text-muted-foreground">
                      {location.address || meta.city}
                    </span>
                  )}
                  <span className="mt-3 block text-sm text-brand-ink">{t("board.info.map")}</span>
                </span>
                <ArrowUpRightIcon
                  className="size-5 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              </button>
            ))
          ) : (
            <p className="px-5 py-5 text-muted-foreground adm:px-6">
              {t("board.info.placePending")}
            </p>
          )}
        </div>
        {(orderedDivisions.length > 0 || settings.setup.singleDivision) && (
          <div className="flex flex-wrap items-center justify-between gap-4 border-t px-5 py-3 text-sm adm:px-6">
            <div className="flex min-w-0 flex-wrap gap-x-5 gap-y-2 text-muted-foreground">
              {orderedDivisions.map((division) => (
                <span key={division.id} className="break-words">
                  {division.name}
                  {pools.some((pool) => pool.divisionId === division.id) && (
                    <span className="ml-2 text-xs">
                      {t("board.info.pools", {
                        names: pools
                          .filter((pool) => pool.divisionId === division.id)
                          .map((pool) => pool.name)
                          .join(", "),
                      })}
                    </span>
                  )}
                </span>
              ))}
              {!orderedDivisions.length &&
                settings.setup.singleDivision &&
                t("board.info.singleDivision")}
            </div>
          </div>
        )}
        <TournamentKeyTimes details={details} />
      </Card>

      <div
        className={
          hasContacts && hasGuides
            ? "grid items-start gap-5 adm:grid-cols-[minmax(0,1fr)_20rem] adm:gap-6"
            : "space-y-5 empty:hidden"
        }
      >
        {hasContacts && (
          <aside className={hasGuides ? "min-w-0 adm:col-start-2 adm:row-start-1" : "min-w-0"}>
            <TournamentContacts details={details} />
          </aside>
        )}
        {hasGuides && (
          <div className="min-w-0 space-y-4 adm:col-start-1 adm:row-start-1">
            {registrationOpen && (
              <Card className="gap-0 p-0">
                <h2 className="px-5 pt-5 text-lg font-semibold">{t("board.info.joinTitle")}</h2>
                <div className="space-y-5 p-4">
                  {meta.registrationCloses && (
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t("tournament.registrationCloses")}
                      </p>
                      <time
                        dateTime={meta.registrationCloses}
                        className="mt-1 block text-base font-semibold"
                      >
                        {eventWhen(meta.registrationCloses)}
                      </time>
                    </div>
                  )}
                  <p className="max-w-prose text-sm leading-relaxed text-muted-foreground">
                    {t("board.info.joinHint")}
                  </p>
                  <Button className="w-full" render={<a href="/account" />}>
                    {t("board.info.chooseClub")}
                    <ChevronRightIcon aria-hidden="true" />
                  </Button>
                  {meta.entryReview && (
                    <p className="text-sm text-muted-foreground">{t("board.info.reviewed")}</p>
                  )}
                  {meta.rosterLocks && (
                    <div className="border-t pt-4">
                      <p className="text-sm text-muted-foreground">{t("tournament.rosterLocks")}</p>
                      <time dateTime={meta.rosterLocks} className="mt-1 block font-medium">
                        {eventWhen(meta.rosterLocks)}
                      </time>
                    </div>
                  )}
                </div>
                <a
                  href={tournamentHref(meta.slug, "/pickups")}
                  className="group flex items-center justify-between gap-4 border-t px-5 py-4 transition-colors hover:bg-accent adm:px-6"
                >
                  <span>
                    <span className="block font-medium">{t("board.info.findTeam")}</span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {t("board.info.pickupHint")}
                    </span>
                  </span>
                  <ChevronRightIcon
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                </a>
              </Card>
            )}
            {meta.information?.text && (
              <Card className="gap-3 p-5">
                <h2 className="flex items-center gap-3 text-lg font-semibold">
                  <InfoIcon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                  {t("eventInfo.practical")}
                </h2>
                <InformationText text={meta.information.text} />
              </Card>
            )}
            <TournamentDetailsInfo details={details} />
            <TournamentMedia media={meta.information?.media ?? []} />
          </div>
        )}
      </div>

      {(schedule.length > 0 || visibleFields.length > 0 || regulations !== null) && (
        <Card className="gap-0 divide-y p-0">
          {schedule.length > 0 && (
            <ResourceLink href={tournamentHref(meta.slug, "#next")}>
              {t("board.info.viewSchedule", { count: schedule.length })}
            </ResourceLink>
          )}
          {visibleFields.map((field) =>
            field.streamUrl ? (
              <ResourceLink key={field.id} href={field.streamUrl} external>
                <span>
                  {fieldNameOf(fields, field.id)}
                  <span className="ml-3 font-normal text-muted-foreground">
                    {t("board.info.stream")}
                  </span>
                </span>
              </ResourceLink>
            ) : (
              <div key={field.id} className="px-5 py-4 text-sm adm:px-6">
                {fieldNameOf(fields, field.id)}
              </div>
            ),
          )}
          {regulations !== null && (
            <ResourceLink href={tournamentHref(meta.slug, "/regulations")}>
              {t("board.regulationsLink")}
            </ResourceLink>
          )}
        </Card>
      )}

      <details className="group board-pane rounded-xl border bg-card">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 adm:px-6 [&::-webkit-details-marker]:hidden">
          <span>
            <span className="block text-base font-medium">{t("board.info.conditions")}</span>
            <span className="mt-1 block text-sm text-muted-foreground">
              {t("board.info.conditionsHint")}
            </span>
          </span>
          <ChevronDownIcon
            aria-hidden="true"
            className="size-5 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
          />
        </summary>
        <div className="space-y-5 border-t px-5 py-5 adm:px-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1 text-sm text-muted-foreground">
              <p>
                {settings.spirit.enabled
                  ? t("board.info.spiritEnabled")
                  : t("board.info.spiritDisabled")}
              </p>
              {settings.spirit.enabled && settings.spirit.deadline && (
                <p>
                  {t("board.info.spiritDeadline", { when: eventWhen(settings.spirit.deadline) })}
                </p>
              )}
            </div>
            <a href="/rules" className="text-sm text-brand-ink underline underline-offset-4">
              {t("board.rulesLink")}
            </a>
          </div>
          <p className="text-sm text-muted-foreground">
            {settings.setup.durationsConfirmed
              ? t("board.info.formatHint")
              : t("board.info.formatProvisional")}
          </p>
          <div className="grid gap-6 adm:grid-cols-2">
            {formats.map((division) => {
              const format = formatFor(settings.format, division.id);
              const game = division.id === undefined ? {} : { divisionId: division.id };
              return (
                <div key={division.id ?? "default"}>
                  {divisions.length > 0 && <h3 className="nxt mb-2 text-xl">{division.name}</h3>}
                  <dl className="divide-y text-sm">
                    <InfoRow label={t("board.info.poolGame")}>
                      {t("board.info.minutes", {
                        count: playMinutes({ ...game, stage: "pool" }, settings.slots),
                      })}
                    </InfoRow>
                    <InfoRow label={t("board.info.bracketGame")}>
                      {t("board.info.minutes", {
                        count: playMinutes({ ...game, stage: "bracket" }, settings.slots),
                      })}
                    </InfoRow>
                    <InfoRow label={t("board.info.finalGame")}>
                      {t("board.info.minutes", {
                        count: playMinutes({ ...game, label: { kind: "final" } }, settings.slots),
                      })}
                    </InfoRow>
                    <InfoRow label={t("board.info.target")}>
                      {format.scoreTarget === null ? t("board.info.noTarget") : format.scoreTarget}
                    </InfoRow>
                    <InfoRow label={t("board.info.timeouts")}>{format.timeoutsPerHalf}</InfoRow>
                    {format.softCapMin !== undefined && (
                      <InfoRow label={t("board.info.softCap")}>
                        {t("board.info.minutes", { count: format.softCapMin })}
                      </InfoRow>
                    )}
                    {format.hardCapMin !== undefined && (
                      <InfoRow label={t("board.info.hardCap")}>
                        {t("board.info.minutes", { count: format.hardCapMin })}
                      </InfoRow>
                    )}
                  </dl>
                </div>
              );
            })}
          </div>
          <p className="text-sm text-muted-foreground">
            {t("board.info.breaks", {
              turnaround: settings.slots.turnaround,
              rest: settings.slots.rest,
            })}
          </p>
        </div>
      </details>
      {MapChooser && selectedLocation && (
        <MapChooser location={selectedLocation} onClose={() => setSelectedLocation(null)} />
      )}
    </>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words font-medium">{children}</dd>
    </div>
  );
}
function ResourceLink({
  href,
  external = false,
  children,
}: {
  href: string;
  external?: boolean;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className="flex min-h-14 items-center justify-between gap-3 px-5 py-4 text-sm font-medium transition-colors hover:bg-accent adm:px-6"
    >
      {children}
      <ArrowUpRightIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
    </a>
  );
}
